import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

export const setEndDateToEndOfDay = (date: Date): Date => {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
};

/**
 * Uploads a file to Supabase Storage and returns the **storage path** (e.g. `userId/file.jpg`).
 * Callers should store this path in the DB. Display code must resolve it to a signed URL
 * via `getSignedEventImageUrl` / `useEventImageSrc` because the bucket is private.
 */
export const uploadImageToStorage = async (
  file: File,
  bucket: string,
  path: string
): Promise<string> => {
  const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });

  if (error) {
    throw error;
  }

  return data.path;
};

export const checkActiveEventsLimit = async (userId: string): Promise<{ allowed: boolean; count: number }> => {
  const { data: activeEvents, error } = await supabase
    .from('events')
    .select('id')
    .eq('created_by', userId)
    .eq('is_active', true);

  if (error) {
    throw new Error('Failed to check existing events. Please try again.');
  }

  return {
    allowed: !activeEvents || activeEvents.length < 3,
    count: activeEvents?.length || 0,
  };
};

export const deleteImageFromStorage = async (imageUrl: string): Promise<boolean> => {
  try {
    if (!imageUrl || !imageUrl.trim()) {
      return true;
    }

    // Extract the path after the bucket name from public URL
    // URL format: https://...supabase.co/storage/v1/object/public/event-images/userId/fileName
    const bucketMatch = imageUrl.match(/event-images\/(.+)$/);
    if (!bucketMatch || !bucketMatch[1]) {
      logger.warn('Could not extract file path from URL:', imageUrl);
      return false;
    }

    const filePath = bucketMatch[1];

    // Delete from Supabase Storage
    const { error } = await supabase.storage
      .from('event-images')
      .remove([filePath]);

    if (error) {
      logger.warn('Failed to delete image:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.warn('Error deleting image:', error);
    return false;
  }
};

export const createEventChat = async (
  eventId: string,
  eventName: string,
  userId: string,
  imageUrl: string | null
): Promise<void> => {
  const { data: chat, error: chatError } = await supabase
    .from('chats')
    .insert([{
      event_id: eventId,
      name: eventName,
      image: imageUrl,
    }])
    .select()
    .single();

  if (chatError) {
    logger.warn('Failed to create chat:', chatError);
    return;
  }

  if (chat) {
    await supabase
      .from('chat_participants')
      .insert([{
        chat_id: chat.id,
        user_id: userId,
        role: 'organization',
        joined_at: new Date().toISOString(),
      }]);

    await supabase
      .from('chats')
      .update({ image: imageUrl })
      .eq('id', chat.id);
  }
};
